import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import baseUrl from "../../../../api/baseUrl";

type SessionType = 1 | 2 | 3 | 4;

type SessionRow = {
  sessionName: string;
  startTime: string;
  endTime: string;
  type: SessionType;
};

const SESSION_TYPE_OPTIONS: SessionType[] = [1, 2, 3, 4];

function normalizeSessionType(v: unknown): SessionType {
  const n = typeof v === "number" ? v : parseInt(String(v ?? ""), 10);
  if (n === 1 || n === 2 || n === 3 || n === 4) return n;
  return 1;
}

type GeneralSessionOption = {
  sessionName: string;
  startTime: string;
  endTime: string;
};

function todayIsoLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function weekdayFromIso(iso: string): string {
  const [y, mo, da] = iso.split("-").map(Number);
  if (!y || !mo || !da) return "";
  return new Date(y, mo - 1, da).toLocaleDateString(undefined, {
    weekday: "long",
  });
}

function formatDisplayDate(iso: string): string {
  const [y, mo, da] = iso.split("-").map(Number);
  if (!y || !mo || !da) return "";
  return new Date(y, mo - 1, da).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function parseSessionsFromApi(raw: unknown): SessionRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item: unknown) => {
      const o = item as Record<string, unknown>;
      return {
        sessionName: String(o?.sessionName ?? "").trim(),
        startTime: String(o?.startTime ?? "").trim(),
        endTime: String(o?.endTime ?? "").trim(),
        type: normalizeSessionType(o?.type),
      };
    })
    .filter((r) => r.sessionName);
}

function buildHalfHourTimeOptions(): string[] {
  const slots: string[] = [];
  for (let h = 0; h < 24; h += 1) {
    const hh = String(h).padStart(2, "0");
    slots.push(`${hh}:00`);
    slots.push(`${hh}:30`);
  }
  return slots;
}

const HALF_HOUR_SLOT_OPTIONS = buildHalfHourTimeOptions();

function timeToMinutes(t: string): number {
  const m = String(t).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return NaN;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

function pickStartInOptions(want: string, options: string[]): string {
  if (options.length === 0) return want;
  if (options.includes(want)) return want;
  const w = timeToMinutes(want);
  if (!Number.isFinite(w)) return options[0] ?? want;
  const sorted = [...options].sort(
    (a, b) => timeToMinutes(a) - timeToMinutes(b)
  );
  const atOrAfter = sorted.find((opt) => timeToMinutes(opt) >= w);
  if (atOrAfter) return atOrAfter;
  return sorted[sorted.length - 1];
}

function pickEndInOptions(want: string, options: string[]): string {
  if (options.length === 0) return want;
  if (options.includes(want)) return want;
  const w = timeToMinutes(want);
  if (!Number.isFinite(w)) return options[options.length - 1] ?? want;
  const sorted = [...options].sort(
    (a, b) => timeToMinutes(a) - timeToMinutes(b)
  );
  const atOrBefore = [...sorted].reverse().find((opt) => timeToMinutes(opt) <= w);
  if (atOrBefore) return atOrBefore;
  return sorted[0];
}

function snapStartToHalfHour(want: string, options: string[]): string {
  const t = String(want ?? "").trim();
  if (!t) return "";
  return pickStartInOptions(t, options);
}

function snapEndToHalfHour(want: string, options: string[]): string {
  const t = String(want ?? "").trim();
  if (!t) return "";
  return pickEndInOptions(t, options);
}

/** 30-minute steps only; scrollable list like General opening hours. */
function HalfHourTimeSelect({
  id,
  ariaLabelledBy,
  value,
  options,
  emptyLabel,
  onChange,
  allowOpen = true,
  onBlockedOpen,
}: {
  id: string;
  ariaLabelledBy: string;
  value: string;
  options: string[];
  emptyLabel: string;
  onChange: (next: string) => void;
  /** When false, the list does not open; use with onBlockedOpen for user feedback. */
  allowOpen?: boolean;
  onBlockedOpen?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!allowOpen) setOpen(false);
  }, [allowOpen]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={`relative ${open ? "z-[70]" : "z-0"}`}
    >
      <button
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open && allowOpen}
        aria-disabled={!allowOpen}
        aria-labelledby={ariaLabelledBy}
        onClick={() => {
          if (!allowOpen) {
            onBlockedOpen?.();
            return;
          }
          setOpen((o) => !o);
        }}
        className={[
          "w-full px-3 py-2 rounded-md border outline-none focus:ring-2 bg-white text-left text-sm flex items-center justify-between gap-2",
          allowOpen
            ? "border-slate-200 focus:border-primary focus:ring-primary/20 text-slate-800 cursor-pointer"
            : "border-slate-200/80 bg-slate-50 text-slate-500 cursor-not-allowed",
        ].join(" ")}
      >
        <span className={value ? "tabular-nums" : "text-slate-400"}>
          {value || emptyLabel}
        </span>
        <svg
          className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden
        >
          <path
            d="m6 9 6 6 6-6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && allowOpen ? (
        <ul
          role="listbox"
          aria-labelledby={ariaLabelledBy}
          className="absolute left-0 right-0 top-full z-[80] mt-1 max-h-52 min-h-0 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-xl ring-1 ring-black/10"
        >
          {options.map((t) => (
            <li
              key={t}
              role="option"
              aria-selected={t === value}
              className={[
                "px-3 py-2 text-sm cursor-pointer select-none tabular-nums",
                t === value
                  ? "bg-slate-100 font-medium text-slate-900"
                  : "text-slate-700 hover:bg-slate-50",
              ].join(" ")}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(t);
                setOpen(false);
              }}
            >
              {t}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function SessionPickerCombobox({
  id,
  labelId,
  options,
  valueSessionName,
  loading,
  onSelect,
}: {
  id: string;
  labelId: string;
  options: GeneralSessionOption[];
  valueSessionName: string;
  loading: boolean;
  onSelect: (opt: GeneralSessionOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const selected =
    options.find((s) => s.sessionName === valueSessionName) ?? null;
  const canOpen = !loading;

  return (
    <div
      ref={rootRef}
      className={`relative ${open ? "z-[70]" : "z-0"}`}
    >
      <button
        type="button"
        id={id}
        disabled={!canOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={labelId}
        onClick={() => canOpen && setOpen((o) => !o)}
        className={[
          "w-full min-h-[2.75rem] px-3 py-2 rounded-lg border text-left text-sm flex items-center justify-between gap-2 transition-shadow",
          "bg-white text-slate-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20",
          loading
            ? "border-slate-200 opacity-60 cursor-not-allowed"
            : "border-slate-200 hover:border-slate-300 cursor-pointer shadow-sm",
        ].join(" ")}
      >
        <div className="min-w-0 flex-1">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500">
              <span
                className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-slate-600"
                aria-hidden
              />
              <span>Loading sessions…</span>
            </div>
          ) : selected ? (
            <>
              <div className="font-semibold text-slate-900 truncate leading-tight">
                {selected.sessionName}
              </div>
              {selected.startTime && selected.endTime ? (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500 tabular-nums">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="h-3.5 w-3.5 shrink-0 text-slate-400"
                    aria-hidden
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" strokeLinecap="round" />
                  </svg>
                  <span>
                    {selected.startTime}
                    <span className="mx-1 text-slate-300">→</span>
                    {selected.endTime}
                  </span>
                </div>
              ) : null}
            </>
          ) : (
            <span className="text-slate-400">Choose a session for this day</span>
          )}
        </div>
        <svg
          className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden
        >
          <path
            d="m6 9 6 6 6-6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && canOpen ? (
        <ul
          role="listbox"
          aria-labelledby={labelId}
          className="absolute left-0 right-0 top-full z-[80] mt-1.5 max-h-56 min-h-0 overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-xl ring-1 ring-black/10"
        >
          {options.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-slate-500">
              No general sessions for this weekday.
              <span className="mt-1 block text-xs text-slate-400">
                Add them under General opening hours first.
              </span>
            </li>
          ) : (
            options.map((s) => {
              const isActive = s.sessionName === valueSessionName;
              return (
                <li
                  key={s.sessionName}
                  role="option"
                  aria-selected={isActive}
                  className={[
                    "mx-1 rounded-md px-2.5 py-2.5 cursor-pointer select-none transition-colors",
                    isActive
                      ? "bg-primary/10 ring-1 ring-primary/25"
                      : "hover:bg-slate-50",
                  ].join(" ")}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onSelect(s);
                    setOpen(false);
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 leading-snug">
                        {s.sessionName}
                      </div>
                      {s.startTime && s.endTime ? (
                        <div className="mt-1 text-xs text-slate-500 tabular-nums">
                          {s.startTime}
                          <span className="mx-1 text-slate-300">–</span>
                          {s.endTime}
                        </div>
                      ) : null}
                    </div>
                    {s.startTime && s.endTime ? (
                      <span className="shrink-0 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600 tabular-nums">
                        {s.startTime}
                        <span className="mx-0.5 font-normal text-slate-400">
                          ·
                        </span>
                        {s.endTime}
                      </span>
                    ) : null}
                  </div>
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}

export default function OpeningHoursTab() {
  const [selectedDate, setSelectedDate] = useState(todayIsoLocal);
  const selectedDay = weekdayFromIso(selectedDate);

  const [rowsByDate, setRowsByDate] = useState<Record<string, SessionRow[]>>(
    {}
  );
  const [openingHoursLoading, setOpeningHoursLoading] = useState(false);
  const rows = rowsByDate[selectedDate] ?? [];

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [sessionModalMode, setSessionModalMode] = useState<"create" | "edit">(
    "create"
  );
  const [editOriginalSessionName, setEditOriginalSessionName] = useState<
    string | null
  >(null);
  const [editOriginalType, setEditOriginalType] = useState<SessionType | null>(
    null
  );
  const [modalDate, setModalDate] = useState(todayIsoLocal);
  const [newSession, setNewSession] = useState<SessionRow>({
    sessionName: "",
    startTime: "",
    endTime: "",
    type: 1,
  });
  const [generalSessions, setGeneralSessions] = useState<GeneralSessionOption[]>(
    []
  );
  const [sessionNamesLoading, setSessionNamesLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoadingSession, setDeleteLoadingSession] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!selectedDate) return;
    let cancelled = false;
    setOpeningHoursLoading(true);
    baseUrl
      .get("/client/setting/openHours/findByDate", {
        params: { date: selectedDate },
      })
      .then((res) => {
        if (cancelled) return;
        const sessions = parseSessionsFromApi(res.data?.data?.sessions);
        setRowsByDate((prev) => ({ ...prev, [selectedDate]: sessions }));
      })
      .catch((err: { response?: { data?: { message?: string; error?: string } } }) => {
        if (cancelled) return;
        setRowsByDate((prev) => ({ ...prev, [selectedDate]: [] }));
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to load opening hours"
        );
      })
      .finally(() => {
        if (!cancelled) setOpeningHoursLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDate]);

  useEffect(() => {
    if (!isCreateOpen || !modalDate) return;

    let cancelled = false;
    setSessionNamesLoading(true);

    baseUrl
      .get("/client/setting/openHours/findSessionNames", {
        params: { date: modalDate },
      })
      .then((res) => {
        if (cancelled) return;
        const raw = res.data?.data?.sessions;
        const list: GeneralSessionOption[] = Array.isArray(raw)
          ? raw
              .map((item: unknown) => {
                const o = item as Record<string, unknown>;
                const sessionName = String(o?.sessionName ?? "").trim();
                const startTime = String(o?.startTime ?? "").trim();
                const endTime = String(o?.endTime ?? "").trim();
                if (!sessionName) return null;
                return { sessionName, startTime, endTime };
              })
              .filter((x): x is GeneralSessionOption => x != null)
          : [];
        setGeneralSessions(list);
        setNewSession((p) => {
          const match = list.find((s) => s.sessionName === p.sessionName);
          if (match) {
            let start = snapStartToHalfHour(
              match.startTime,
              HALF_HOUR_SLOT_OPTIONS
            );
            let end = snapEndToHalfHour(match.endTime, HALF_HOUR_SLOT_OPTIONS);
            const sm = timeToMinutes(start);
            const em = timeToMinutes(end);
            if (
              Number.isFinite(sm) &&
              Number.isFinite(em) &&
              em <= sm
            ) {
              end =
                HALF_HOUR_SLOT_OPTIONS.find((t) => timeToMinutes(t) > sm) ??
                end;
            }
            return {
              ...p,
              sessionName: match.sessionName,
              startTime: start,
              endTime: end,
            };
          }
          if (sessionModalMode === "edit" && p.sessionName.trim()) {
            return p;
          }
          return { ...p, sessionName: "", startTime: "", endTime: "", type: 1 };
        });
      })
      .catch((err: { response?: { data?: { message?: string; error?: string } } }) => {
        if (cancelled) return;
        setGeneralSessions([]);
        if (sessionModalMode === "edit") {
          toast.error(
            err?.response?.data?.message ||
              err?.response?.data?.error ||
              "Failed to load session names"
          );
          return;
        }
        setNewSession((p) => ({
          ...p,
          sessionName: "",
          startTime: "",
          endTime: "",
          type: 1,
        }));
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to load session names"
        );
      })
      .finally(() => {
        if (!cancelled) setSessionNamesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isCreateOpen, modalDate, sessionModalMode]);

  /** Earliest allowed start from general-hours template for the selected session. */
  const sessionMinStart = useMemo(() => {
    const name = newSession.sessionName.trim();
    if (!name) return null;
    const g = generalSessions.find((s) => s.sessionName === name);
    if (!g?.startTime?.trim()) return null;
    return snapStartToHalfHour(g.startTime, HALF_HOUR_SLOT_OPTIONS);
  }, [newSession.sessionName, generalSessions]);

  /** Latest allowed end from general-hours template for the selected session. */
  const sessionMaxEnd = useMemo(() => {
    const name = newSession.sessionName.trim();
    if (!name) return null;
    const g = generalSessions.find((s) => s.sessionName === name);
    if (!g?.endTime?.trim()) return null;
    return snapEndToHalfHour(g.endTime, HALF_HOUR_SLOT_OPTIONS);
  }, [newSession.sessionName, generalSessions]);

  const startHalfHourOptions = useMemo(() => {
    const endM = timeToMinutes(newSession.endTime);
    const minM =
      sessionMinStart != null ? timeToMinutes(sessionMinStart) : NaN;

    let opts = HALF_HOUR_SLOT_OPTIONS;
    if (Number.isFinite(minM)) {
      opts = opts.filter((t) => timeToMinutes(t) >= minM);
    }
    if (Number.isFinite(endM)) {
      opts = opts.filter((t) => timeToMinutes(t) < endM);
    }
    if (opts.length > 0) return opts;
    if (Number.isFinite(minM)) {
      return HALF_HOUR_SLOT_OPTIONS.filter((t) => timeToMinutes(t) >= minM);
    }
    if (Number.isFinite(endM)) {
      const beforeEnd = HALF_HOUR_SLOT_OPTIONS.filter(
        (t) => timeToMinutes(t) < endM
      );
      return beforeEnd.length > 0 ? beforeEnd : HALF_HOUR_SLOT_OPTIONS;
    }
    return HALF_HOUR_SLOT_OPTIONS;
  }, [newSession.endTime, sessionMinStart]);

  useEffect(() => {
    if (!isCreateOpen || !newSession.sessionName.trim()) return;
    const t = newSession.startTime;
    if (!t || startHalfHourOptions.length === 0) return;
    if (startHalfHourOptions.includes(t)) return;
    const next = startHalfHourOptions[0];
    setNewSession((p) => {
      let end = p.endTime;
      const nextM = timeToMinutes(next);
      const endM = timeToMinutes(end);
      const maxM =
        sessionMaxEnd != null ? timeToMinutes(sessionMaxEnd) : NaN;
      if (!Number.isFinite(endM) || endM <= nextM) {
        let after = HALF_HOUR_SLOT_OPTIONS.filter(
          (slot) => timeToMinutes(slot) > nextM
        );
        if (Number.isFinite(maxM)) {
          after = after.filter((slot) => timeToMinutes(slot) <= maxM);
        }
        end =
          after.length > 0
            ? after[0]
            : HALF_HOUR_SLOT_OPTIONS.find(
                (slot) => timeToMinutes(slot) > nextM
              ) ?? end;
      }
      return { ...p, startTime: next, endTime: end };
    });
  }, [
    isCreateOpen,
    newSession.sessionName,
    newSession.startTime,
    startHalfHourOptions,
    sessionMaxEnd,
  ]);

  const endHalfHourOptions = useMemo(() => {
    const startM = timeToMinutes(newSession.startTime);
    const maxM =
      sessionMaxEnd != null ? timeToMinutes(sessionMaxEnd) : NaN;

    let opts = HALF_HOUR_SLOT_OPTIONS;
    if (Number.isFinite(startM)) {
      opts = opts.filter((t) => timeToMinutes(t) > startM);
    }
    if (Number.isFinite(maxM)) {
      opts = opts.filter((t) => timeToMinutes(t) <= maxM);
    }
    if (opts.length > 0) return opts;
    if (Number.isFinite(startM)) {
      const after = HALF_HOUR_SLOT_OPTIONS.filter(
        (t) => timeToMinutes(t) > startM
      );
      return after.length > 0 ? after : HALF_HOUR_SLOT_OPTIONS;
    }
    if (Number.isFinite(maxM)) {
      const upToMax = HALF_HOUR_SLOT_OPTIONS.filter(
        (t) => timeToMinutes(t) <= maxM
      );
      return upToMax.length > 0 ? upToMax : HALF_HOUR_SLOT_OPTIONS;
    }
    return HALF_HOUR_SLOT_OPTIONS;
  }, [newSession.startTime, sessionMaxEnd]);

  useEffect(() => {
    if (!isCreateOpen || !newSession.sessionName.trim()) return;
    const t = newSession.endTime;
    if (!t || endHalfHourOptions.length === 0) return;
    if (endHalfHourOptions.includes(t)) return;
    const next = endHalfHourOptions[endHalfHourOptions.length - 1];
    setNewSession((p) => ({ ...p, endTime: next }));
  }, [
    isCreateOpen,
    newSession.sessionName,
    newSession.endTime,
    endHalfHourOptions,
  ]);

  const openCreate = () => {
    setSessionModalMode("create");
    setEditOriginalSessionName(null);
    setEditOriginalType(null);
    setModalDate(selectedDate);
    setNewSession({ sessionName: "", startTime: "", endTime: "", type: 1 });
    setIsCreateOpen(true);
  };

  const openEdit = (row: SessionRow) => {
    setSessionModalMode("edit");
    setEditOriginalSessionName(row.sessionName.trim());
    setEditOriginalType(normalizeSessionType(row.type));
    setModalDate(selectedDate);
    let start = snapStartToHalfHour(row.startTime, HALF_HOUR_SLOT_OPTIONS);
    let end = snapEndToHalfHour(row.endTime, HALF_HOUR_SLOT_OPTIONS);
    const sm = timeToMinutes(start);
    const em = timeToMinutes(end);
    if (Number.isFinite(sm) && Number.isFinite(em) && em <= sm) {
      end =
        HALF_HOUR_SLOT_OPTIONS.find((t) => timeToMinutes(t) > sm) ?? end;
    }
    setNewSession({
      sessionName: row.sessionName,
      startTime: start,
      endTime: end,
      type: normalizeSessionType(row.type),
    });
    setIsCreateOpen(true);
  };

  const closeCreate = () => {
    setIsCreateOpen(false);
    setSessionModalMode("create");
    setEditOriginalSessionName(null);
    setEditOriginalType(null);
    setNewSession({ sessionName: "", startTime: "", endTime: "", type: 1 });
  };

  const refreshRowsForDate = async (iso: string) => {
    const listRes = await baseUrl.get("/client/setting/openHours/findByDate", {
      params: { date: iso },
    });
    const sessions = parseSessionsFromApi(listRes.data?.data?.sessions);
    setRowsByDate((prev) => ({ ...prev, [iso]: sessions }));
  };

  const onDeleteSession = async (row: SessionRow) => {
    if (!selectedDate) return;
    const name = row.sessionName.trim();
    const type = normalizeSessionType(row.type);
    const deleteKey = `${name}__${type}`;
    if (
      !window.confirm(
        `Delete “${name}” (type ${type}) on ${formatDisplayDate(selectedDate)}?`
      )
    ) {
      return;
    }
    setDeleteLoadingSession(deleteKey);
    try {
      await baseUrl.post("/client/setting/openHours/deleteSession", {
        date: selectedDate,
        sessionName: name,
        type,
      });
      await refreshRowsForDate(selectedDate);
      toast.success("Session deleted");
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { message?: string; error?: string } };
      };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Could not delete session"
      );
    } finally {
      setDeleteLoadingSession(null);
    }
  };

  const onSaveSession = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed: SessionRow = {
      sessionName: newSession.sessionName.trim(),
      startTime: newSession.startTime.trim(),
      endTime: newSession.endTime.trim(),
      type: normalizeSessionType(newSession.type),
    };
    if (!trimmed.sessionName || !modalDate) return;
    if (!trimmed.startTime || !trimmed.endTime) {
      toast.error("Choose start and end times.");
      return;
    }

    const payload: {
      date: string;
      sessionName: string;
      startTime: string;
      endTime: string;
      type: SessionType;
      active: boolean;
      oldSessionName?: string;
      oldType?: SessionType;
    } = {
      date: modalDate,
      sessionName: trimmed.sessionName,
      startTime: trimmed.startTime,
      endTime: trimmed.endTime,
      type: trimmed.type,
      active: true,
    };
    if (
      sessionModalMode === "edit" &&
      editOriginalSessionName &&
      (editOriginalSessionName.trim() !== trimmed.sessionName ||
        (editOriginalType != null && editOriginalType !== trimmed.type))
    ) {
      payload.oldSessionName = editOriginalSessionName.trim();
      if (editOriginalType != null) payload.oldType = editOriginalType;
    }

    setSaveLoading(true);
    try {
      if (sessionModalMode === "edit") {
        payload.oldSessionName = editOriginalSessionName?.trim() ?? payload.oldSessionName;
        if (payload.oldType == null && editOriginalType != null) {
          payload.oldType = editOriginalType;
        }
        await baseUrl.post("/client/setting/openHours/updateSession", payload);
      } else {
        await baseUrl.post("/client/setting/openHours/createSession", payload);
      }
      await refreshRowsForDate(modalDate);
      setSelectedDate(modalDate);
      toast.success(
        sessionModalMode === "edit" ? "Session updated" : "Session saved"
      );
      closeCreate();
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { message?: string; error?: string } };
      };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Could not save session"
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const isEditModal = sessionModalMode === "edit";

  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden sm:mx-auto sm:max-w-6xl">
      <div className="px-4 py-3 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-800">Opening hours</h3>
      </div>

      <div className="px-4 pt-3 pb-2 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1.5">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            Set by date
          </div>
          <label className="flex flex-col gap-1 sm:inline-flex sm:flex-row sm:items-center sm:gap-2">
            <span className="sr-only">Select date</span>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-1.5 rounded-md text-sm border border-slate-200 bg-white text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
            />
          </label>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-end">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{selectedDay}</span>
            {selectedDate ? (
              <span className="text-slate-500"> · {formatDisplayDate(selectedDate)}</span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors w-full sm:w-auto"
          >
            Create Session
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-center">
          <thead>
            <tr className="bg-slate-50/80">
              <th className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[180px]">
                Session Name
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[120px]">
                Start Time
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[120px]">
                End Time
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[72px]">
                Type
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[1%] whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {openingHoursLoading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-2 py-8 text-sm text-slate-500 text-center"
                >
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-2 py-8 text-sm text-slate-500 text-center"
                >
                  No sessions for this date.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={`${r.sessionName}__${r.type}`}
                  className="hover:bg-slate-50/50"
                >
                  <td className="px-2 py-2 text-sm font-medium text-slate-700">
                    {r.sessionName}
                  </td>
                  <td className="px-2 py-2 text-sm text-slate-600">
                    {r.startTime}
                  </td>
                  <td className="px-2 py-2 text-sm text-slate-600">
                    {r.endTime}
                  </td>
                  <td className="px-2 py-2 text-sm text-slate-600 tabular-nums">
                    {r.type}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <div className="inline-flex items-center justify-center gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(r)}
                        disabled={Boolean(deleteLoadingSession)}
                        className="px-2 py-1 rounded text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeleteSession(r)}
                        disabled={
                          deleteLoadingSession ===
                          `${r.sessionName.trim()}__${normalizeSessionType(
                            r.type
                          )}`
                        }
                        className="px-2 py-1 rounded text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                      >
                        {deleteLoadingSession ===
                        `${r.sessionName.trim()}__${normalizeSessionType(
                          r.type
                        )}`
                          ? "…"
                          : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isCreateOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={closeCreate}
            aria-label="Close modal"
          />
          <div className="relative z-10 w-full max-w-sm sm:max-w-md">
            <form
              onSubmit={onSaveSession}
              className="rounded-xl bg-white shadow-lg border border-slate-200 overflow-visible"
            >
              <div className="rounded-t-xl px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-white">
                <div className="text-sm font-semibold text-slate-800">
                  {isEditModal ? "Edit session" : "Create session"}
                </div>
                <button
                  type="button"
                  onClick={closeCreate}
                  aria-label="Close"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-4 space-y-3 overflow-visible">
                <label className="space-y-1 block">
                  <div className="text-xs font-semibold text-slate-600">Date</div>
                  <input
                    type="date"
                    value={modalDate}
                    onChange={(e) => setModalDate(e.target.value)}
                    disabled={isEditModal}
                    className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-slate-800 outline-none focus:border-primary disabled:bg-slate-50 disabled:text-slate-500"
                    required
                  />
                  {modalDate ? (
                    <div className="text-xs text-slate-500 pt-0.5">
                      {weekdayFromIso(modalDate)}
                      <span className="text-slate-400">
                        {" "}
                        · {formatDisplayDate(modalDate)}
                      </span>
                    </div>
                  ) : null}
                </label>

                <div className="space-y-1 block">
                  <div
                    id="create-session-label"
                    className="text-xs font-semibold text-slate-600"
                  >
                    Session
                  </div>
                  <SessionPickerCombobox
                    id="create-session-picker"
                    labelId="create-session-label"
                    options={generalSessions}
                    valueSessionName={newSession.sessionName}
                    loading={sessionNamesLoading}
                    onSelect={(opt) => {
                      let start = snapStartToHalfHour(
                        opt.startTime,
                        HALF_HOUR_SLOT_OPTIONS
                      );
                      let end = snapEndToHalfHour(
                        opt.endTime,
                        HALF_HOUR_SLOT_OPTIONS
                      );
                      const sm = timeToMinutes(start);
                      const em = timeToMinutes(end);
                      if (
                        Number.isFinite(sm) &&
                        Number.isFinite(em) &&
                        em <= sm
                      ) {
                        end =
                          HALF_HOUR_SLOT_OPTIONS.find(
                            (t) => timeToMinutes(t) > sm
                          ) ?? end;
                      }
                      setNewSession((p) => ({
                        ...p,
                        sessionName: opt.sessionName,
                        startTime: start,
                        endTime: end,
                      }));
                    }}
                  />
                </div>

                <label className="space-y-1 block">
                  <div className="text-xs font-semibold text-slate-600">Type</div>
                  <select
                    value={newSession.type}
                    onChange={(e) =>
                      setNewSession((p) => ({
                        ...p,
                        type: normalizeSessionType(e.target.value),
                      }))
                    }
                    className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-slate-800 outline-none focus:border-primary"
                  >
                    {SESSION_TYPE_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <div
                      id="create-start-time-label"
                      className="text-xs font-semibold text-slate-600"
                    >
                      Start time
                    </div>
                    <HalfHourTimeSelect
                      id="create-start-time"
                      ariaLabelledBy="create-start-time-label"
                      value={newSession.sessionName ? newSession.startTime : ""}
                      options={startHalfHourOptions}
                      emptyLabel="Start time"
                      allowOpen={Boolean(newSession.sessionName.trim())}
                      onBlockedOpen={() =>
                        toast.error("Select a session first, then choose times.")
                      }
                      onChange={(next) => {
                        setNewSession((p) => {
                          let end = p.endTime;
                          const nextM = timeToMinutes(next);
                          const endM = timeToMinutes(end);
                          const maxM =
                            sessionMaxEnd != null
                              ? timeToMinutes(sessionMaxEnd)
                              : NaN;
                          if (
                            !Number.isFinite(endM) ||
                            endM <= nextM
                          ) {
                            let after = HALF_HOUR_SLOT_OPTIONS.filter(
                              (t) => timeToMinutes(t) > nextM
                            );
                            if (Number.isFinite(maxM)) {
                              after = after.filter(
                                (t) => timeToMinutes(t) <= maxM
                              );
                            }
                            end =
                              after.length > 0
                                ? after[0]
                                : HALF_HOUR_SLOT_OPTIONS.find(
                                    (t) => timeToMinutes(t) > nextM
                                  ) ?? end;
                          }
                          return { ...p, startTime: next, endTime: end };
                        });
                      }}
                    />
                  </div>

                  <div className="space-y-1">
                    <div
                      id="create-end-time-label"
                      className="text-xs font-semibold text-slate-600"
                    >
                      End time
                    </div>
                    <HalfHourTimeSelect
                      id="create-end-time"
                      ariaLabelledBy="create-end-time-label"
                      value={newSession.sessionName ? newSession.endTime : ""}
                      options={endHalfHourOptions}
                      emptyLabel="End time"
                      allowOpen={Boolean(newSession.sessionName.trim())}
                      onBlockedOpen={() =>
                        toast.error("Select a session first, then choose times.")
                      }
                      onChange={(next) =>
                        setNewSession((p) => {
                          let start = p.startTime;
                          const endM = timeToMinutes(next);
                          const startM = timeToMinutes(start);
                          const minM =
                            sessionMinStart != null
                              ? timeToMinutes(sessionMinStart)
                              : NaN;
                          if (
                            Number.isFinite(endM) &&
                            Number.isFinite(startM) &&
                            startM >= endM
                          ) {
                            let candidates = HALF_HOUR_SLOT_OPTIONS.filter(
                              (slot) => timeToMinutes(slot) < endM
                            );
                            if (Number.isFinite(minM)) {
                              candidates = candidates.filter(
                                (slot) => timeToMinutes(slot) >= minM
                              );
                            }
                            start =
                              candidates.length > 0
                                ? candidates[candidates.length - 1]
                                : start;
                          }
                          return { ...p, endTime: next, startTime: start };
                        })
                      }
                    />
                  </div>
                </div>

                {!newSession.sessionName.trim() ? (
                  <p
                    className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-2.5 py-2"
                    role="status"
                  >
                    Select a session above before opening the time dropdowns.
                  </p>
                ) : null}
              </div>

              <div className="rounded-b-xl px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-white">
                <button
                  type="button"
                  onClick={closeCreate}
                  className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saveLoading}
                  className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saveLoading
                    ? isEditModal
                      ? "Updating…"
                      : "Saving…"
                    : isEditModal
                      ? "Update"
                      : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
